/**
 * Infrastructure statistics calculation from infragraph format
 *
 * These functions are used by InfoPanel to display infrastructure overview statistics
 */

import { InfragraphInfrastructure } from '../types/infragraph';
import { InfrastructureStats } from '../types/visualization';
import { EdgeExpander } from './edgeExpander';

/**
 * Calculate infrastructure statistics from infragraph data
 */
export function calculateInfrastructureStats(infrastructure: InfragraphInfrastructure): InfrastructureStats {
  // Count unique device types
  const deviceTypes = new Set(infrastructure.devices.map(d => d.name));

  // Extract locations from instances
  const sites = new Set<string>();
  const pods = new Set<string>();
  const networks = new Set<string>();
  const racks = new Set<string>();

  infrastructure.instances.forEach(instance => {
    if (instance.location) {
      const loc = instance.location;
      if (loc.site) sites.add(loc.site);
      if (loc.site && loc.pod) pods.add(`${loc.site}/${loc.pod}`);
      if (loc.site && loc.pod && loc.network) networks.add(`${loc.site}/${loc.pod}/${loc.network}`);
      if (loc.site && loc.pod && loc.rack) racks.add(`${loc.site}/${loc.pod}/${loc.rack}`);
    }
  });

  // Count components by type
  const componentsByType: Record<string, number> = {};
  let totalComponents = 0;

  infrastructure.devices.forEach(device => {
    device.components.forEach(component => {
      const typeName = component.name;
      componentsByType[typeName] = (componentsByType[typeName] || 0) + component.count;
      totalComponents += component.count;
    });
  });

  // Count links by name/type (from top-level and device-level)
  const linksByProtocol: Record<string, number> = {};

  // Count top-level links
  if (infrastructure.links) {
    infrastructure.links.forEach(link => {
      const linkType = link.name || 'unknown';
      linksByProtocol[linkType] = (linksByProtocol[linkType] || 0) + 1;
    });
  }

  // Count device-level links
  infrastructure.devices.forEach(device => {
    if (device.links) {
      device.links.forEach(link => {
        const linkType = link.name || 'unknown';
        linksByProtocol[linkType] = (linksByProtocol[linkType] || 0) + 1;
      });
    }
  });

  // Sum up instance counts
  const totalInstances = infrastructure.instances.reduce((sum, instance) => sum + instance.count, 0);

  // Expand edges to get actual connection count
  const edgeExpander = new EdgeExpander();
  let totalConnections = 0;
  infrastructure.edges.forEach(edge => {
    const expandedEdges = edgeExpander.expandInfrastructureEdge(edge);
    totalConnections += expandedEdges.length;
  });

  return {
    totalDevices: infrastructure.devices.length,
    totalInstances,
    totalComponents,
    totalConnections,
    deviceTypes: deviceTypes.size,
    sites: sites.size,
    pods: pods.size,
    networks: networks.size,
    racks: racks.size,
    componentsByType,
    linksByProtocol
  };
}
